import { useMutation, useQuery } from "@apollo/client";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import DomainAdd from "@mui/icons-material/DomainAdd";
import EditNote from "@mui/icons-material/EditNote";
import Label from "@mui/icons-material/Label";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { S3FileLink } from "components/S3FileLink";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  WastePipelineRunMetadataQuery,
  WastePipelineRunRowsQuery,
  WastePipelineRunRejectedRowsQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { inProgressJobStatuses } from "hooks/useLongPollingForJob";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import {
  INGEST_WASTE_PIPELINE_DATA_MUTATION,
  UPDATE_PIPELINE_OUTPUT_DATA_MUTATION,
  WASTE_PIPELINE_RUN_METADATA_QUERY,
  WASTE_PIPELINE_RUN_ROWS_QUERY,
  WASTE_PIPELINE_RUN_REJECTED_ROWS_QUERY,
} from "./api";
import { BulkEditColumnDialog } from "./BulkEditColumnDialog";
import { FacilityMatchDialog } from "./FacilityMatchDialog";
import { ManagePipelineOutputTagsDialog } from "./ManagePipelineOutputTags";
import { getIngestionAlertSeverity, getIngestionStatusColor } from "./pipelineUtils";

type ReviewRowBase = WastePipelineRunRowsQuery["wastePipelineRunRows"]["items"][number];
type RejectedRowBase =
  WastePipelineRunRejectedRowsQuery["wastePipelineRunRejectedRows"]["items"][number];
type ReviewRow = ReviewRowBase;
type RejectedRow = RejectedRowBase;

export const PipelineRunResults = () => {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const alerts = useAlerts();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ingestResult, setIngestResult] = useState<{ jobId: string } | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [facilityDialogRow, setFacilityDialogRow] = useState<RejectedRow | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });
  const [manageTagsRows, setManageTagsRows] = useState<
    | {
        id: string;
        tags: string[];
        reportable?: string | null;
        overrideManualEdits?: string | null;
      }[]
    | null
  >(null);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState<HTMLElement | null>(null);
  const [bulkEditColumn, setBulkEditColumn] = useState<{
    fieldName: string;
    fieldLabel: string;
    options: { value: string; label: string }[];
    rows: {
      id: string;
      currentValue?: string | null;
      tags?: string[] | null;
      reportable?: string | null;
      overrideManualEdits?: string | null;
    }[];
  } | null>(null);

  // Metadata + pipeline info
  const {
    data: metadataData,
    loading: metadataLoading,
    refetch: refetchMetadata,
  } = useQuery(WASTE_PIPELINE_RUN_METADATA_QUERY, {
    variables: { pipelineId: pipelineId ?? "" },
    skip: !pipelineId,
  });

  const metadata = metadataData?.wastePipelineRunMetadata;
  const pipeline = metadataData?.pipeline;

  // Default to Output Files tab when there are no matched or rejected rows
  const hasSetDefaultTab = useRef(false);
  useEffect(() => {
    if (hasSetDefaultTab.current || metadataLoading) return;
    const matched = metadata?.matchedRows ?? 0;
    const rejected = metadata?.rejectedRowCount ?? 0;
    if (matched === 0 && rejected === 0) {
      setActiveTab(2);
    }
    hasSetDefaultTab.current = true;
  }, [metadata, metadataLoading]);

  // All matched rows (no skip/take — return everything for client-side pagination)
  const { data: rowsData, loading: rowsLoading } = useQuery(WASTE_PIPELINE_RUN_ROWS_QUERY, {
    variables: { pipelineId: pipelineId ?? "" },
    skip: !pipelineId || activeTab !== 0,
  });

  // All rejected rows (no skip/take)
  const { data: rejectedData, loading: rejectedLoading } = useQuery(
    WASTE_PIPELINE_RUN_REJECTED_ROWS_QUERY,
    {
      variables: { pipelineId: pipelineId ?? "" },
      skip: !pipelineId || activeTab !== 1,
    }
  );

  const [ingest, { loading: ingestLoading }] = useMutation(INGEST_WASTE_PIPELINE_DATA_MUTATION);
  const [updatePipelineOutput] = useMutation(UPDATE_PIPELINE_OUTPUT_DATA_MUTATION);

  const processRowUpdate = useCallback(
    async (newRow: ReviewRow, oldRow: ReviewRow) => {
      const norm = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
      const changedFields: Record<string, unknown> = {};
      for (const key of Object.keys(newRow) as (keyof ReviewRow)[]) {
        if (key === "id" || key === "__typename") continue;
        if (norm(newRow[key]) !== norm(oldRow[key])) changedFields[key] = newRow[key];
      }
      if (Object.keys(changedFields).length > 0) {
        try {
          await updatePipelineOutput({
            variables: { id: newRow.id, input: changedFields },
            refetchQueries: [{ query: WASTE_PIPELINE_RUN_ROWS_QUERY, variables: { pipelineId } }],
          });
          alerts.success("Saved", { autoHideDuration: 2000 });
        } catch (err) {
          alerts.error(`Failed to save: ${err instanceof Error ? err.message : "unknown error"}`);
          throw err;
        }
      }
      return newRow;
    },
    [updatePipelineOutput, alerts, pipelineId]
  );

  const handleIngestConfirm = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const result = await ingest({
        variables: { pipelineId },
      });
      const jobId = result.data?.ingestWastePipelineData?.id;
      if (jobId) {
        setIngestResult({ jobId });
      }
      setIngestError(null);
      setConfirmOpen(false);
      refetchMetadata();
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : "Ingestion failed");
      setConfirmOpen(false);
    }
  }, [pipelineId, ingest, refetchMetadata]);

  const ingestionJobInProgress =
    !!pipeline?.ingestionJob?.status &&
    inProgressJobStatuses.includes(pipeline.ingestionJob.status);

  const pipelineName = pipeline?.name ?? "Pipeline";

  useBreadcrumb([
    { label: "Pipelines", to: "/staff/pipelines" },
    { label: pipelineName },
    { label: "Results" },
  ]);

  const reviewColumns = useReviewColumns();
  const rejectedColumns = useRejectedColumns(setFacilityDialogRow);

  const reviewRows = useMemo(() => rowsData?.wastePipelineRunRows.items ?? [], [rowsData]);

  const selectedRows = useMemo(() => {
    if (rowSelectionModel.type !== "include") return reviewRows;
    return reviewRows.filter((row) => rowSelectionModel.ids.has(row.id));
  }, [rowSelectionModel, reviewRows]);

  const rejectedRows = useMemo(
    () => rejectedData?.wastePipelineRunRejectedRows.items ?? [],
    [rejectedData]
  );

  if (!pipelineId) {
    return <Typography>No pipeline ID provided.</Typography>;
  }

  if (metadataLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={8}>
        <CircularProgress />
      </Stack>
    );
  }

  const matchedRowCount = metadata?.matchedRows ?? rowsData?.wastePipelineRunRows.count ?? 0;
  const rejectedRowCount =
    metadata?.rejectedRowCount ?? rejectedData?.wastePipelineRunRejectedRows.count ?? 0;

  return (
    <Stack spacing={3} py={3} px={2}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button component={Link} to="/staff/pipelines" startIcon={<ArrowBack />}>
          Back to Pipelines
        </Button>
        <Typography variant="h5">{pipelineName} — Results</Typography>
        {pipeline?.tenant?.name && (
          <Typography variant="subtitle1" color="text.secondary">
            {pipeline.tenant.name}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={ingestLoading || ingestionJobInProgress || matchedRowCount === 0}
        >
          Ingest Pipeline
        </Button>
      </Stack>

      {ingestResult && (
        <Alert severity="success" onClose={() => setIngestResult(null)}>
          Ingestion job created.{" "}
          <Link to={`/staff/jobs/${ingestResult.jobId}`}>View job status</Link>
        </Alert>
      )}
      {ingestError && (
        <Alert severity="error" onClose={() => setIngestError(null)}>
          {ingestError}
        </Alert>
      )}

      {pipeline?.ingestionJob && (
        <IngestionStatusBanner
          jobId={pipeline.ingestionJob.id}
          status={pipeline.ingestionJob.status}
          ingestedAt={pipeline.ingestedAt}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleIngestConfirm}
        loading={ingestLoading}
        header="Ingest Pipeline Data"
        info={
          pipeline?.ingestedAt
            ? `This pipeline was previously ingested on ${prettyPrintDateTime(pipeline.ingestedAt)}. Re-ingesting will overwrite existing data.`
            : `This will import ${matchedRowCount.toLocaleString()} matched rows into the system for ${pipeline?.tenant?.name ?? "this tenant"}.`
        }
        confirmText="Ingest"
        confirmStartIcon={<PublishIcon />}
      />

      {/* Summary Stats (only shown when lastRunSummary exists) */}
      {metadata && <MetadataSummary metadata={metadata} />}

      {/* Tabs: Matched Rows / Rejected Rows / Output Files */}
      <Box>
        <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ flexGrow: 1 }}
          >
            <Tab label={`Matched Rows (${matchedRowCount.toLocaleString()})`} />
            <Tab label={`Rejected Rows (${rejectedRowCount.toLocaleString()})`} />
            <Tab label="Output Files" />
          </Tabs>
          {activeTab === 0 && (
            <>
              <Button
                endIcon={<ArrowDropDown />}
                onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                disabled={selectedRows.length === 0}
                size="small"
                variant="outlined"
              >
                Bulk Actions ({selectedRows.length})
              </Button>
              <Menu
                anchorEl={bulkActionsAnchor}
                open={!!bulkActionsAnchor}
                onClose={() => setBulkActionsAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setManageTagsRows(
                      selectedRows.map((row) => ({
                        id: row.id,
                        tags: row.tags ?? [],
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      }))
                    );
                  }}
                >
                  <ListItemIcon>
                    <Label fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Manage Tags</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setBulkEditColumn({
                      fieldName: "reportable",
                      fieldLabel: "Reportable",
                      options: [
                        { value: "Reportable", label: "Reportable" },
                        { value: "Not Reportable", label: "Not Reportable" },
                      ],
                      rows: selectedRows.map((row) => ({
                        id: row.id,
                        currentValue: row.reportable,
                        tags: row.tags,
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      })),
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditNote fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Set Reportable</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setBulkEditColumn({
                      fieldName: "overrideManualEdits",
                      fieldLabel: "Override Manual Edits",
                      options: [
                        { value: "Y", label: "Yes" },
                        { value: "N", label: "No" },
                      ],
                      rows: selectedRows.map((row) => ({
                        id: row.id,
                        currentValue: row.overrideManualEdits,
                        tags: row.tags,
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      })),
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditNote fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Set Override Manual Edits</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>

        {activeTab === 0 && (
          <>
            <Box sx={{ height: "calc(100vh - 480px)", minHeight: 400, mt: 2 }}>
              <DataGrid
                rows={reviewRows}
                columns={reviewColumns}
                loading={rowsLoading}
                pageSizeOptions={[50, 100, 250, 500]}
                initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                getRowId={(row) => row.id}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
                noRowsMessage="No matched rows found"
                pinnedColumns={{
                  left: ["__check__"],
                  right: ["tags", "reportable", "overrideManualEdits"],
                }}
                processRowUpdate={processRowUpdate}
                autosizeOptions={{ includeHeaders: true }}
                onCellDoubleClick={(params: GridCellParams) => {
                  if (params.field === "tags") {
                    const row = params.row as ReviewRow;
                    setManageTagsRows([
                      {
                        id: row.id,
                        tags: row.tags ?? [],
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      },
                    ]);
                  }
                }}
              />
            </Box>
            {manageTagsRows && pipeline?.tenantId && (
              <ManagePipelineOutputTagsDialog
                selectedRows={manageTagsRows}
                tenantId={pipeline.tenantId}
                dialogOpen={!!manageTagsRows}
                onClose={() => {
                  setManageTagsRows(null);
                  setRowSelectionModel({ type: "include", ids: new Set() });
                }}
              />
            )}
            {bulkEditColumn && (
              <BulkEditColumnDialog
                open
                onClose={() => {
                  setBulkEditColumn(null);
                  setRowSelectionModel({ type: "include", ids: new Set() });
                }}
                fieldName={bulkEditColumn.fieldName}
                fieldLabel={bulkEditColumn.fieldLabel}
                options={bulkEditColumn.options}
                selectedRows={bulkEditColumn.rows}
              />
            )}
          </>
        )}

        {activeTab === 1 && (
          <Box sx={{ height: "calc(100vh - 480px)", minHeight: 400, mt: 2 }}>
            <DataGrid
              rows={rejectedRows}
              columns={rejectedColumns}
              loading={rejectedLoading}
              pageSizeOptions={[50, 100, 250, 500]}
              initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
              getRowId={(row) => row.id}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={() => "auto"}
              disableRowSelectionOnClick
              noRowsMessage="No rejected rows found"
            />
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ mt: 2 }}>
            {pipeline && <PipelineDetailsSummary pipeline={pipeline} />}

            <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
              Output Files ({pipeline?.outputFiles?.length ?? 0})
            </Typography>
            {pipeline?.outputFiles && pipeline.outputFiles.length > 0 ? (
              <Stack spacing={1}>
                {pipeline.outputFiles.map((fileKey) => (
                  <S3FileLink key={fileKey} fileKey={fileKey} />
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No output files available.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Facility match dialog */}
      {facilityDialogRow?.extractedAddress && pipeline?.tenantId && (
        <FacilityMatchDialog
          open
          onClose={() => setFacilityDialogRow(null)}
          extractedAddress={facilityDialogRow.extractedAddress}
          tenantId={pipeline.tenantId}
        />
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Metadata Summary — single compact card
// ---------------------------------------------------------------------------

type MetadataType = NonNullable<WastePipelineRunMetadataQuery["wastePipelineRunMetadata"]>;

function MetadataSummary({ metadata }: { metadata: MetadataType }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        {/* Run info */}
        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Vendor: <strong>{metadata.vendorSource}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Run Key: <strong>{metadata.runKey}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Generated:{" "}
            <strong>
              {metadata.generatedAt ? prettyPrintDateTime(metadata.generatedAt) : undefined}
            </strong>
          </Typography>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Key counts + classification side by side */}
        <Stack direction="row" spacing={8} flexWrap="wrap" useFlexGap>
          {/* Counts */}
          <Box sx={{ minWidth: 200, flex: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Row Counts
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="Source Rows" value={metadata.sourceRows} />
                <StatRow label="Matched" value={metadata.matchedRows} />
                <StatRow label="Rejected" value={metadata.rejectedRowCount} />
                <StatRow label="Unique Manifests" value={metadata.uniqueManifests} />
                <StatRow label="Unique Facilities" value={metadata.uniqueFacilities} />
              </TableBody>
            </Table>
          </Box>

          {/* Classification */}
          <Box sx={{ minWidth: 220, flex: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Classification
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="RCRA Hazardous" value={metadata.classificationRcraHazardous} />
                <StatRow
                  label="Acutely Hazardous"
                  value={metadata.classificationRcraAcutelyHazardous}
                />
                <StatRow label="State Regulated" value={metadata.classificationStateRegulated} />
                <StatRow label="Nonhazardous" value={metadata.classificationNonhazardous} />
              </TableBody>
            </Table>
          </Box>

          {/* Reportability */}
          <Box sx={{ minWidth: 180, flex: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Reportability
            </Typography>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 0.25, px: 1 } }}>
              <TableBody>
                <StatRow label="Reportable" value={metadata.reportable} />
                <StatRow label="Not Reportable" value={metadata.notReportable} />
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatRow({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <TableRow>
      <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
      <TableCell align="right">
        <strong>{value?.toLocaleString()}</strong>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Pipeline details — mirrors info from the old PipelineDialog
// ---------------------------------------------------------------------------

type PipelineType = NonNullable<WastePipelineRunMetadataQuery["pipeline"]>;

function PipelineDetailsSummary({ pipeline }: { pipeline: PipelineType }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Pipeline Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Pipeline ID
            </Typography>
            <CopyableTypography variant="body2" text={pipeline.id} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body2">{pipeline.name}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tenant
            </Typography>
            <Typography variant="body2">{pipeline.tenant?.name ?? "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Last Run
            </Typography>
            <Typography variant="body2">
              {pipeline.lastRunAt ? prettyPrintDateTime(pipeline.lastRunAt) : "Never"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">{prettyPrintDateTime(pipeline.createdAt)}</Typography>
            <Typography variant="body2">{pipeline.createdBy}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Updated
            </Typography>
            <Typography variant="body2">{prettyPrintDateTime(pipeline.updatedAt)}</Typography>
            <Typography variant="body2">{pipeline.updatedBy}</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Input Files ({pipeline.inputFiles?.length ?? 0})
            </Typography>
            {pipeline.inputFiles && pipeline.inputFiles.length > 0 ? (
              <Box>
                {pipeline.inputFiles.map((file) => (
                  <S3FileLink key={file} fileKey={file} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No input files
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ingestion status banner
// ---------------------------------------------------------------------------

function IngestionStatusBanner({
  jobId,
  status,
  ingestedAt,
}: {
  jobId: string;
  status: string;
  ingestedAt?: string | null;
}) {
  const severity = getIngestionAlertSeverity(status);

  return (
    <Alert severity={severity} icon={false} sx={{ alignItems: "center" }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2">
          <strong>Ingestion:</strong>
        </Typography>
        <Chip size="small" label={status} color={getIngestionStatusColor(status)} />
        <Typography variant="body2" color="text.secondary">
          Job: {jobId}
        </Typography>
        {ingestedAt && status === "SUCCEEDED" && (
          <Typography variant="body2" color="text.secondary">
            Completed: {prettyPrintDateTime(ingestedAt)}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Rejected row detail panel (shows rawData JSON)
// ---------------------------------------------------------------------------

function getDetailPanelContent({ row }: { row: RejectedRow }) {
  if (!row.rawData) return null;
  return (
    <Box sx={{ p: 2, bgcolor: "grey.50" }}>
      <Typography variant="subtitle2" gutterBottom>
        Original Row Data
      </Typography>
      <Box
        component="pre"
        sx={{
          fontSize: "0.75rem",
          maxHeight: 400,
          overflow: "auto",
          bgcolor: "grey.100",
          p: 1.5,
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(row.rawData, null, 2)}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useReviewColumns(): GridColDef<ReviewRow>[] {
  return useMemo(
    () => [
      { field: "manifestTrackingNumber", headerName: "Manifest Tracking Number", editable: true },
      { field: "shippedDate", headerName: "Shipped Date", editable: true },
      { field: "receivedDate", headerName: "Received Date", editable: true },
      { field: "facilityId", headerName: "Encamp Facility ID", editable: true },
      { field: "generatorId", headerName: "Generator EPA ID", editable: true },
      { field: "generatorName", headerName: "Generator Name", editable: true },
      {
        field: "generatorLocationStreetNo",
        headerName: "Generator Location Street Number",
        editable: true,
      },
      {
        field: "generatorLocationStreet1",
        headerName: "Generator Location Street 1",
        editable: true,
      },
      {
        field: "generatorLocationStreet2",
        headerName: "Generator Location Street 2",
        editable: true,
      },
      { field: "generatorLocationCity", headerName: "Generator Location City", editable: true },
      { field: "generatorLocationZip", headerName: "Generator Location Zip", editable: true },
      { field: "generatorLocationState", headerName: "Generator Location State", editable: true },
      { field: "desFacilityId", headerName: "Designated Facility EPA ID", editable: true },
      { field: "desFacilityName", headerName: "Designated Facility Name", editable: true },
      { field: "vendorSource", headerName: "Vendor Source", editable: true },
      { field: "vendorSiteId", headerName: "Vendor Site ID", editable: true },
      { field: "wasteLineNumber", headerName: "Waste Line Number", editable: true },
      { field: "dotHazardous", headerName: "U.S. DOT Hazardous Indicator", editable: true },
      { field: "dotIdNumberDescription", headerName: "U.S. DOT Description", editable: true },
      { field: "dotPrintedInformation", headerName: "DOT Printed Information", editable: true },
      {
        field: "nonHazWasteDescription",
        headerName: "Non-Hazardous Waste Description",
        editable: true,
      },
      {
        field: "containerNumber",
        headerName: "Number of Containers",
        type: "number",
        editable: true,
      },
      { field: "containerTypeCode", headerName: "Container Type Code", editable: true },
      { field: "quantity", headerName: "Waste Quantity", editable: true },
      {
        field: "qtyUnitOfMeasureCode",
        headerName: "Quantity Unit of Measure Code",
        editable: true,
      },
      { field: "managementMethodCode", headerName: "Management Method Code", editable: true },
      {
        field: "managementMethodDescription",
        headerName: "Management Method Description",
        editable: true,
      },
      {
        field: "finalManagementMethodCode",
        headerName: "Final Management Method Code",
        editable: true,
      },
      {
        field: "finalManagementMethodDescription",
        headerName: "Final Management Method Description",
        editable: true,
      },
      { field: "brDensity", headerName: "Waste Density", editable: true },
      {
        field: "brDensityUomCode",
        headerName: "Waste Density Unit of Measure Code",
        editable: true,
      },
      {
        field: "brDensityUomDescription",
        headerName: "Waste Density Unit of Measure Description",
        editable: true,
      },
      { field: "brFormCode", headerName: "Form Code", editable: true },
      { field: "brSourceCode", headerName: "Source Code", editable: true },
      { field: "epaWaste", headerName: "EPA Waste Indicator", editable: true },
      { field: "federalWasteCodes", headerName: "Federal Waste Codes", editable: true },
      { field: "stateWasteCodes", headerName: "State Waste Codes", editable: true },
      { field: "classification", headerName: "Classification", editable: true },
      { field: "vendorProfileNumber", headerName: "Vendor Profile Number", editable: true },
      {
        field: "vendorProfileDescription",
        headerName: "Vendor Profile Description",
        editable: true,
      },
      { field: "vendorComments", headerName: "Vendor Comments", editable: true },
      { field: "transporter1Id", headerName: "Transporter 1 EPA ID", editable: true },
      { field: "transporter1Name", headerName: "Transporter 1 Name", editable: true },
      {
        field: "transporter1DaysInPossession",
        headerName: "Transporter 1 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter2Id", headerName: "Transporter 2 EPA ID", editable: true },
      { field: "transporter2Name", headerName: "Transporter 2 Name", editable: true },
      {
        field: "transporter2DaysInPossession",
        headerName: "Transporter 2 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter3Id", headerName: "Transporter 3 EPA ID", editable: true },
      { field: "transporter3Name", headerName: "Transporter 3 Name", editable: true },
      {
        field: "transporter3DaysInPossession",
        headerName: "Transporter 3 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter4Id", headerName: "Transporter 4 EPA ID", editable: true },
      { field: "transporter4Name", headerName: "Transporter 4 Name", editable: true },
      {
        field: "transporter4DaysInPossession",
        headerName: "Transporter 4 Days In Possession",
        type: "number",
        editable: true,
      },
      {
        field: "tags",
        headerName: "Waste Line Tags",
        minWidth: 200,
        renderCell: ({ value }: GridRenderCellParams) => {
          const tags = value as string[] | null;
          if (!tags || tags.length === 0) return null;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          );
        },
      },
      {
        field: "reportable",
        headerName: "Reportable",
        editable: true,
        type: "singleSelect",
        valueOptions: ["Reportable", "Not Reportable"],
        minWidth: 150,
        renderCell: ({ value }: GridRenderCellParams) => {
          if (!value) return null;
          return (
            <Chip
              label={value}
              size="small"
              variant="outlined"
              color={value === "Reportable" ? "success" : "default"}
              sx={{ pointerEvents: "none" }}
            />
          );
        },
      },
      {
        field: "overrideManualEdits",
        headerName: "Override Manual Edits",
        editable: true,
        type: "singleSelect",
        valueOptions: [
          { value: "Y", label: "Yes" },
          { value: "N", label: "No" },
        ],
        minWidth: 170,
        renderCell: ({ value }: GridRenderCellParams) => {
          if (!value) return null;
          const isYes = value === "Y";
          return (
            <Chip
              label={isYes ? "Yes" : "No"}
              size="small"
              variant="outlined"
              color={isYes ? "warning" : "default"}
              sx={{ pointerEvents: "none" }}
            />
          );
        },
      },
    ],
    []
  );
}

function formatExtractedAddress(addr: RejectedRow["extractedAddress"]): string {
  if (!addr) return "—";
  const parts: string[] = [];
  if (addr.name) parts.push(addr.name);

  const location: string[] = [];
  if (addr.streetAddress) location.push(addr.streetAddress);
  if (addr.city) location.push(addr.city);
  if (addr.state && addr.zip) {
    location.push(`${addr.state} ${addr.zip}`);
  } else if (addr.state) {
    location.push(addr.state);
  } else if (addr.zip) {
    location.push(addr.zip);
  }

  if (location.length > 0) {
    parts.push(location.join(", "));
  }
  if (addr.epaId) parts.push(`EPA: ${addr.epaId}`);
  if (addr.vendorCode) parts.push(`Code: ${addr.vendorCode}`);

  return parts.join(" — ") || "—";
}

function useRejectedColumns(onFindFacility: (row: RejectedRow) => void): GridColDef<RejectedRow>[] {
  return useMemo(
    () => [
      { field: "rejectionReason", headerName: "Rejection Reason", flex: 1, minWidth: 300 },
      { field: "rejectionCategory", headerName: "Category", width: 180 },
      {
        field: "extractedAddress",
        headerName: "Address",
        width: 300,
        renderCell: (params: GridRenderCellParams<RejectedRow>) =>
          formatExtractedAddress(params.row.extractedAddress),
      },
      {
        field: "_actions",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<RejectedRow>) => {
          if (!params.row.extractedAddress) return null;
          return (
            <Tooltip title="Search for matching facility">
              <IconButton size="small" color="primary" onClick={() => onFindFacility(params.row)}>
                <DomainAdd fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      { field: "sourceFile", headerName: "Source File", width: 200 },
      { field: "sourceLineNumber", headerName: "Line #", width: 100, type: "number" },
    ],
    [onFindFacility]
  );
}
